import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs'
import { climbToAncestor } from '@schmancy/mixins/discovery.service'
import type { OverlayEntry } from './overlay.types'

/**
 * Whether `node` sits in the exempt layer: it or an ancestor in the flattened
 * tree carries `data-overlay-exempt`. `schmancy-menu`, `schmancy-boat`, the
 * date field, the date range and the navigation-rail puck build their overlay's
 * content root from a host and mark it when that host is in the layer, so the
 * overlay each opens belongs to the layer its host lives in.
 */
export function inExemptLayer(node: Element): boolean {
	return !!climbToAncestor(node, el => el.hasAttribute('data-overlay-exempt'))
}

/**
 * Overlay stack — single source of truth for "what is currently open."
 *
 * A module-scope BehaviorSubject. Reactive pipelines inside the overlay
 * service and public `openOverlays$` observable project off this.
 *
 * Scroll lock and the stack-aware inert manager are both derived from
 * the stack state — no separate mutable counter variables: the stack IS
 * the state; scroll lock / inert are declarative projections.
 */

const stack$$ = new BehaviorSubject<readonly OverlayEntry[]>([])

/** Public read-only stream of the current stack. Emits on every push/pop. */
export const stack$: Observable<readonly OverlayEntry[]> = stack$$.asObservable()

/** Synchronous snapshot. Use only when a component can't subscribe. */
export function currentStack(): readonly OverlayEntry[] {
	return stack$$.value
}

export function pushEntry(entry: OverlayEntry): void {
	stack$$.next([...stack$$.value, entry])
}

/** Remove by id. No-op if the id is not in the stack. */
export function removeEntry(id: string): void {
	const current = stack$$.value
	const next = current.filter(e => e.id !== id)
	if (next.length !== current.length) {
		stack$$.next(next)
	}
}

/** Clear the entire stack. */
export function clearStack(): void {
	if (stack$$.value.length > 0) {
		stack$$.next([])
	}
}

/* ---------------- scroll lock -------------------------------------------- */

/**
 * Body scroll lock, active whenever ANY modal-tier overlay is in the
 * stack. Popover-tier (Tier 1/2) overlays do NOT lock body scroll — a
 * menu / share card / picker shouldn't freeze the page scroll behind it.
 *
 * Subscription is idempotent — subscribing multiple times won't stack
 * effects, because it's a distinctUntilChanged boolean projection.
 *
 * Inner overlay close does NOT release the lock while an outer modal is
 * still open.
 */
let scrollLockActive = false
let previousOverflow = ''
let previousScrollbarGutter = ''

stack$$
	.pipe(
		map(s => s.some(e => e.modal)),
		distinctUntilChanged(),
	)
	.subscribe(shouldLock => {
		if (typeof document === 'undefined') return

		if (shouldLock && !scrollLockActive) {
			previousOverflow = document.documentElement.style.overflow
			previousScrollbarGutter = document.documentElement.style.getPropertyValue('scrollbar-gutter')
			document.documentElement.style.overflow = 'hidden'
			document.documentElement.style.setProperty('scrollbar-gutter', 'stable')
			scrollLockActive = true
		} else if (!shouldLock && scrollLockActive) {
			document.documentElement.style.overflow = previousOverflow
			if (previousScrollbarGutter) {
				document.documentElement.style.setProperty('scrollbar-gutter', previousScrollbarGutter)
			} else {
				document.documentElement.style.removeProperty('scrollbar-gutter')
			}
			previousOverflow = ''
			previousScrollbarGutter = ''
			scrollLockActive = false
		}
	})

/* ---------------- stack-aware inert -------------------------------------- */

/**
 * When a modal overlay opens, every sibling outside its host subtree goes
 * `inert` so AT and keyboard focus can't reach them, and comes back when the
 * last modal that wanted it inert has closed.
 *
 * Note: native `<dialog>.showModal()` already inerts the rest of the
 * document automatically. This is a safety net for:
 *   - anchored (popover) overlays which are non-modal by design but may
 *     carry `modal: true` as the escape hatch;
 *   - stacked overlays where an inner modal opens above a non-modal —
 *     the sibling-inert is a no-op but the invariant still holds.
 *
 * WHY A COUNT AND NOT A FLAG. Modals stack, and the stack does not unwind in
 * order: a photo viewer opened from inside a sheet can outlive the sheet that
 * opened it. Both swept the same siblings, so "is anything still holding this
 * inert" has no answer a per-overlay list can give — the sheet's own release
 * would hand the page back to the keyboard while the viewer is still covering
 * it. The count is that answer, and it lives here, module-scope, because it is
 * a fact about the whole stack rather than about any one overlay.
 *
 * `wasInert` is the other half: a sibling an author already inerted for reasons
 * of their own must be left inert when the count reaches zero, not "restored"
 * into being reachable.
 */
const inertClaims = new Map<HTMLElement, { count: number; wasInert: boolean }>()

/**
 * A sibling carrying `data-overlay-exempt` is left live: a layer in `<body>`
 * beside the app — a dev tool, an inspector — must keep taking taps under any
 * modal, including the one it opened itself. A native modal `<dialog>` exempts
 * later top-layer elements; this sweep has no top layer to read, so the
 * exemption is declared.
 *
 * An element already inert BECAUSE AN EARLIER MODAL CLAIMED IT is still
 * sweepable — that is the nested case, and refusing it there is what left the
 * count at one and let the first overlay to close un-inert the page under a
 * later one. An element inert for any other reason is not this sweep's to
 * touch, and `inertClaims` is how the two are told apart.
 */
export function inertableSibling(child: Element, host: Element): child is HTMLElement {
	if (child === host || !(child instanceof HTMLElement)) return false
	if (child.hasAttribute('data-overlay-exempt')) return false
	return !child.inert || inertClaims.has(child)
}

/**
 * Claim `element` inert on behalf of one modal overlay. Every claim is paired
 * with exactly one `releaseInert`, and the element is handed back only on the
 * last of them.
 */
export function claimInert(element: HTMLElement): void {
	const claim = inertClaims.get(element)
	if (claim) {
		claim.count++
	} else {
		inertClaims.set(element, { count: 1, wasInert: element.inert })
	}
	element.inert = true
}

export function releaseInert(element: HTMLElement): void {
	const claim = inertClaims.get(element)
	if (!claim) return
	claim.count--
	if (claim.count > 0) return
	inertClaims.delete(element)
	element.inert = claim.wasInert
}

/* ---------------- overlayEvents multicast helper ------------------------- */

/**
 * Returns an Observable of `tagName` elements currently in the stack.
 * The overlayEvents public helper composes `fromEvent` over this stream
 * via switchMap to tap events without owning the overlay lifecycle.
 *
 * Stays alive across open/close cycles — the caller owns completion via
 * `takeUntil(this.disconnecting)`, matching the house rxjs convention.
 */
export function elementsByTag$(tagName: string): Observable<readonly HTMLElement[]> {
	const lower = tagName.toLowerCase()
	return stack$$.pipe(
		map(entries => {
			const matches: HTMLElement[] = []
			for (const entry of entries) {
				// `#overlay-mount` — where every content form renders — lives in
				// `<schmancy-overlay>`'s own shadow root, and a light-DOM
				// querySelector on a shadow host never crosses into its own
				// shadowRoot. Searching `entry.element` directly finds nothing, for
				// every overlay, silently.
				const inner = entry.element.shadowRoot?.querySelector<HTMLElement>(lower)
				if (inner) matches.push(inner)
			}
			return matches
		}),
		distinctUntilChanged((a, b) => a.length === b.length && a.every((el, i) => el === b[i])),
	)
}
